import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdminLayout from '@/components/admin/AdminLayout';
import {
    Plus, Edit2, Trash2, Power, Upload, FileText,
    Link as LinkIcon, X, ChevronLeft, Save, Play,
    FileUp, Youtube, CheckCircle2, Loader2, Video
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import { safeFetch } from '@/lib/safeFetch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from '@/lib/utils';

interface AdminCourse {
    id: string;
    title: string;
    description: string;
    thumbnail: string;
    status: 'active' | 'draft';
}

interface Lesson {
    id: string;
    course_id: string;
    title: string;
    video_url: string;
    description: string;
    duration: string;
    order_index: number;
}

interface LessonAsset {
    id: string;
    lesson_id: string;
    title: string;
    file_url: string;
    type: 'pdf' | 'link';
}

const AdminCourseEditor = () => {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const [course, setCourse] = useState<AdminCourse | null>(null);
    const [lessons, setLessons] = useState<Lesson[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState<{ [key: string]: boolean }>({});

    // Lesson Edit State
    const [editingLessonId, setEditingLessonId] = useState<string | 'new' | null>(null);
    const [currentLesson, setCurrentLesson] = useState<Partial<Lesson>>({});
    const [videoType, setVideoType] = useState<'url' | 'upload'>('url');

    // Asset State
    const [managingLessonAssets, setManagingLessonAssets] = useState<string | null>(null);
    const [newAsset, setNewAsset] = useState<Partial<LessonAsset>>({ type: 'pdf' });

    useEffect(() => {
        if (courseId) {
            loadCourseData();
        }
    }, [courseId]);

    const loadCourseData = async () => {
        setIsLoading(true);
        try {
            const courseData = await safeFetch<AdminCourse | null>(
                supabase.from('courses').select('*').eq('id', courseId).single(),
                null,
                'Loading course'
            );

            if (courseData) {
                setCourse(courseData);
                const lessonsData = await safeFetch<Lesson[]>(
                    supabase.from('lessons').select('*').eq('course_id', courseId).order('order_index', { ascending: true }),
                    [],
                    'Loading lessons'
                );
                setLessons(lessonsData);
            } else {
                toast.error('Course not found');
                navigate('/admin/courses');
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileUpload = async (file: File, bucket: string, path: string, statusKey: string) => {
        setIsUploading(true);
        try {
            const { error: uploadError } = await supabase.storage
                .from(bucket)
                .upload(path, file, { upsert: true });

            if (uploadError) {
                // Check for size limit error
                if (uploadError.message?.includes('exceeded the maximum allowed size')) {
                    toast.error("DATABASE ACTION REQUIRED: Run INCREASE_STORAGE_LIMITS.sql in Supabase SQL Editor to allow large uploads.", { duration: 10000 });
                    return null;
                }
                throw uploadError;
            }

            const { data: { publicUrl } } = supabase.storage
                .from(bucket)
                .getPublicUrl(path);

            setUploadStatus(prev => ({ ...prev, [statusKey]: true }));
            toast.success('Upload Successful ✅');
            return publicUrl;
        } catch (error: any) {
            toast.error(`Error: ${error.message}`);
            return null;
        } finally {
            setIsUploading(false);
        }
    };

    const saveCourseDetails = async () => {
        if (!course || !course.title) return;
        setIsSaving(true);
        try {
            const { error } = await supabase.from('courses').update({
                title: course.title,
                description: course.description,
                thumbnail: course.thumbnail,
                status: course.status
            }).eq('id', course.id);

            if (error) throw error;
            toast.success('Course details updated');
        } catch (error: any) {
            toast.error('Failed to save changes');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveLesson = async () => {
        if (!courseId || !currentLesson.title) return;
        setIsSaving(true);
        try {
            const lessonData = {
                course_id: courseId,
                title: currentLesson.title,
                video_url: currentLesson.video_url,
                description: currentLesson.description || '',
                duration: currentLesson.duration || '',
                order_index: currentLesson.order_index || (lessons.length + 1)
            };

            if (editingLessonId && editingLessonId !== 'new') {
                const { error } = await supabase.from('lessons').update(lessonData).eq('id', editingLessonId);
                if (error) throw error;
                toast.success('Lesson updated');
            } else {
                const { error } = await supabase.from('lessons').insert([lessonData]);
                if (error) throw error;
                toast.success('New lesson added');
            }
            setEditingLessonId(null);
            loadCourseData();
        } catch (error: any) {
            toast.error('Failed to save lesson');
        } finally {
            setIsSaving(false);
        }
    };

    const deleteLesson = async (id: string) => {
        if (!confirm('Are you sure you want to delete this lesson?')) return;
        try {
            const { error } = await supabase.from('lessons').delete().eq('id', id);
            if (error) throw error;
            toast.success('Lesson deleted');
            loadCourseData();
        } catch (error) {
            toast.error('Failed to delete lesson');
        }
    };

    const handleAddAsset = async () => {
        if (!managingLessonAssets || !newAsset.title || !newAsset.file_url) return;
        try {
            const { error } = await supabase.from('lesson_assets').insert([{
                lesson_id: managingLessonAssets,
                title: newAsset.title,
                file_url: newAsset.file_url,
                type: newAsset.type
            }]);
            if (error) throw error;
            toast.success('File added successfully');
            setNewAsset({ type: 'pdf' });
            // Refresh assets list? or just reload all
            const assets = await safeFetch<LessonAsset[]>(
                supabase.from('lesson_assets').select('*').eq('lesson_id', managingLessonAssets),
                [],
                'Loading files'
            );
            // We need a local state for actual assets in the modal or just use query
        } catch (error) {
            toast.error('Failed to add file');
        }
    };

    if (isLoading) {
        return (
            <AdminLayout>
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                    <Loader2 className="w-10 h-10 text-accent animate-spin" />
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Loading Course Data...</p>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="space-y-10">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="flex items-center gap-6">
                        <Link to="/admin/courses">
                            <button className="p-3 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-primary transition-all shadow-sm">
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                        </Link>
                        <div>
                            <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">Course Editor</h2>
                            <p className="text-slate-500 font-medium text-sm">Editing {course?.title}</p>
                        </div>
                    </div>
                    <Button onClick={saveCourseDetails} disabled={isSaving} className="btn-navy h-14 rounded-2xl px-10 text-[10px] font-black uppercase tracking-widest shadow-premium">
                        {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Save Changes
                    </Button>
                </div>

                <Tabs defaultValue="details" className="w-full">
                    <TabsList className="bg-slate-100 p-1.5 rounded-2xl mb-10 w-fit">
                        <TabsTrigger value="details" className="rounded-xl px-8 py-2.5 text-[10px] font-black uppercase tracking-widest data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm">Course info</TabsTrigger>
                        <TabsTrigger value="lessons" className="rounded-xl px-8 py-2.5 text-[10px] font-black uppercase tracking-widest data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm">Lesson list</TabsTrigger>
                    </TabsList>

                    <TabsContent value="details" className="mt-0">
                        <div className="grid lg:grid-cols-3 gap-10">
                            <div className="lg:col-span-2 bg-white p-10 rounded-[2.5rem] border border-slate-100 shadow-sm space-y-8">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Course Title</label>
                                    <Input
                                        value={course?.title || ''}
                                        onChange={e => setCourse(prev => prev ? { ...prev, title: e.target.value } : null)}
                                        className="h-14 rounded-2xl border-slate-200 focus:border-accent"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Description</label>
                                    <Textarea
                                        value={course?.description || ''}
                                        onChange={e => setCourse(prev => prev ? { ...prev, description: e.target.value } : null)}
                                        className="min-h-[200px] rounded-[1.5rem] border-slate-200 focus:border-accent p-6"
                                    />
                                </div>
                            </div>
                            <div className="space-y-8">
                                <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-6 px-1">Course Thumbnail</label>
                                    <div className="space-y-6">
                                        <div className="aspect-[16/10] bg-slate-50 rounded-2xl overflow-hidden border border-slate-200 relative group">
                                            {course?.thumbnail ? (
                                                <img src={course.thumbnail} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                                                    <Upload className="w-10 h-10 mb-2 opacity-20" />
                                                    <span className="text-[8px] font-black uppercase tracking-[0.2em]">No Image</span>
                                                </div>
                                            )}
                                        </div>
                                        <input
                                            type="file"
                                            id="thumb-upload"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={async (e) => {
                                                const file = e.target.files?.[0];
                                                if (file && courseId) {
                                                    const timestamp = Date.now();
                                                    const path = `courses/${courseId}/thumbnail-${timestamp}.png`;
                                                    const url = await handleFileUpload(file, 'course-thumbnails', path, 'thumb');
                                                    if (url) setCourse(prev => prev ? { ...prev, thumbnail: url } : null);
                                                }
                                            }}
                                        />
                                        <label htmlFor="thumb-upload" className={cn(
                                            "flex items-center justify-center gap-3 h-14 bg-slate-50 border border-dashed border-slate-200 rounded-2xl cursor-pointer hover:bg-slate-100 transition-all text-[10px] font-black uppercase tracking-widest text-slate-600",
                                            isUploading && "pointer-events-none opacity-50"
                                        )}>
                                            {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                            {uploadStatus.thumb ? 'Image Updated ✅' : 'Change Image'}
                                        </label>
                                    </div>
                                </div>

                                <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-4 px-1">Course Status</label>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setCourse(prev => prev ? { ...prev, status: 'active' } : null)}
                                            className={cn(
                                                "flex-1 h-12 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all",
                                                course?.status === 'active' ? "bg-emerald-500 text-white shadow-md shadow-emerald-200" : "bg-slate-50 text-slate-400"
                                            )}
                                        >Active</button>
                                        <button
                                            onClick={() => setCourse(prev => prev ? { ...prev, status: 'draft' } : null)}
                                            className={cn(
                                                "flex-1 h-12 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all",
                                                course?.status === 'draft' ? "bg-amber-500 text-white shadow-md shadow-amber-200" : "bg-slate-50 text-slate-400"
                                            )}
                                        >Draft</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="lessons" className="mt-0">
                        <div className="grid lg:grid-cols-12 gap-10">
                            {/* Lessons List */}
                            <div className="lg:col-span-5 space-y-6">
                                <div className="flex items-center justify-between mb-2 px-1">
                                    <h3 className="text-xl font-black text-slate-900 uppercase tracking-tighter">Lesson List</h3>
                                    <Button onClick={() => { setEditingLessonId('new'); setCurrentLesson({ order_index: lessons.length + 1 }); }} className="h-10 rounded-xl px-4 text-[9px] font-black uppercase tracking-widest bg-accent text-accent-foreground hover:bg-accent/90 transition-all">
                                        <Plus className="w-3.5 h-3.5 mr-1" /> Add New Lesson
                                    </Button>
                                </div>
                                <div className="space-y-4">
                                    {lessons.map((lesson, idx) => (
                                        <div
                                            key={lesson.id}
                                            onClick={() => { setEditingLessonId(lesson.id); setCurrentLesson(lesson); }}
                                            className={cn(
                                                "p-6 bg-white rounded-2xl border transition-all cursor-pointer group flex items-center justify-between",
                                                editingLessonId === lesson.id ? "border-accent shadow-md shadow-accent/5 ring-4 ring-accent/5" : "border-slate-100 hover:border-slate-300"
                                            )}
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={cn(
                                                    "w-10 h-10 rounded-xl flex items-center justify-center border font-black text-[10px]",
                                                    editingLessonId === lesson.id ? "bg-accent text-white border-accent" : "bg-slate-50 text-slate-400 border-slate-100"
                                                )}>
                                                    {lesson.order_index}
                                                </div>
                                                <div>
                                                    <h5 className="font-black text-[11px] uppercase tracking-tight text-slate-900 line-clamp-1">{lesson.title}</h5>
                                                    <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">{lesson.duration || '00:00'} Duration</span>
                                                </div>
                                            </div>
                                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); deleteLesson(lesson.id); }}
                                                    className="p-2 text-slate-300 hover:text-destructive transition-colors"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    {lessons.length === 0 && !editingLessonId && (
                                        <div className="p-10 border-2 border-dashed border-slate-100 rounded-[2rem] text-center text-slate-300">
                                            <p className="text-[10px] font-black uppercase tracking-widest italic">No lessons added yet</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Lesson Editor */}
                            <div className="lg:col-span-7">
                                {editingLessonId ? (
                                    <div className="bg-white p-10 rounded-[2.5rem] border border-slate-100 shadow-sm space-y-10 sticky top-28 animate-in fade-in slide-in-from-right-4 duration-500">
                                        <div className="flex items-center justify-between">
                                            <h4 className="text-2xl font-black text-slate-900 uppercase tracking-tighter">
                                                {editingLessonId === 'new' ? 'Add New Lesson' : 'Edit Lesson'}
                                            </h4>
                                            <button onClick={() => setEditingLessonId(null)} className="p-2 hover:bg-slate-50 rounded-lg text-slate-400 transition-all"><X className="w-5 h-5" /></button>
                                        </div>

                                        <div className="space-y-8">
                                            <div className="grid grid-cols-3 gap-6">
                                                <div className="col-span-2 space-y-2">
                                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Lesson Title</label>
                                                    <Input
                                                        placeholder="Introduction to..."
                                                        value={currentLesson.title || ''}
                                                        onChange={e => setCurrentLesson({ ...currentLesson, title: e.target.value })}
                                                        className="h-14 rounded-2xl border-slate-200 focus:border-accent"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Order</label>
                                                    <Input
                                                        type="number"
                                                        value={currentLesson.order_index || 0}
                                                        onChange={e => setCurrentLesson({ ...currentLesson, order_index: parseInt(e.target.value) })}
                                                        className="h-14 rounded-2xl border-slate-200 focus:border-accent"
                                                    />
                                                </div>
                                            </div>

                                            <div className="space-y-4">
                                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Lesson Video</label>
                                                <div className="flex gap-2 p-1.5 bg-slate-100 rounded-2xl w-fit">
                                                    <button
                                                        onClick={() => setVideoType('url')}
                                                        className={cn(
                                                            "flex items-center gap-2 px-6 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all",
                                                            videoType === 'url' ? "bg-white text-primary shadow-sm" : "text-slate-400 hover:text-slate-600"
                                                        )}
                                                    >
                                                        <Youtube className="w-3.5 h-3.5" /> Youtube Link
                                                    </button>
                                                    <button
                                                        onClick={() => setVideoType('upload')}
                                                        className={cn(
                                                            "flex items-center gap-2 px-6 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all",
                                                            videoType === 'upload' ? "bg-white text-primary shadow-sm" : "text-slate-400 hover:text-slate-600"
                                                        )}
                                                    >
                                                        <Video className="w-3.5 h-3.5" /> MP4 Upload
                                                    </button>
                                                </div>

                                                {videoType === 'url' ? (
                                                    <div className="space-y-2">
                                                        <Input
                                                            placeholder="https://youtube.com/embed/..."
                                                            value={currentLesson.video_url || ''}
                                                            onChange={e => setCurrentLesson({ ...currentLesson, video_url: e.target.value })}
                                                            className="h-14 rounded-2xl border-slate-200 focus:border-accent"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="space-y-4">
                                                        <input
                                                            type="file"
                                                            id="video-upload"
                                                            className="hidden"
                                                            accept="video/mp4"
                                                            onChange={async (e) => {
                                                                const file = e.target.files?.[0];
                                                                if (file && courseId) {
                                                                    if (!currentLesson.id) {
                                                                        toast.error('Save lesson details first before uploading video.');
                                                                        return;
                                                                    }

                                                                    const timestamp = Date.now();
                                                                    const path = `courses/${courseId}/lessons/${currentLesson.id}/video-${timestamp}.mp4`;
                                                                    const url = await handleFileUpload(file, 'course-videos', path, 'video');

                                                                    if (url) {
                                                                        setCurrentLesson({ ...currentLesson, video_url: url });

                                                                        // Automatic Persistence to DB
                                                                        if (currentLesson.id !== 'new') {
                                                                            const { error: syncError } = await supabase
                                                                                .from('lessons')
                                                                                .update({ video_url: url })
                                                                                .eq('id', currentLesson.id);

                                                                            if (!syncError) {
                                                                                toast.success('Database Synchronized');
                                                                                loadCourseData(); // Refresh list to show playback availability
                                                                            } else {
                                                                                console.error('Persistence Error:', syncError);
                                                                            }
                                                                        }
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                        <label htmlFor="video-upload" className={cn(
                                                            "flex flex-col items-center justify-center gap-3 p-10 bg-slate-50 border-2 border-dashed border-slate-200 rounded-[2rem] cursor-pointer hover:bg-slate-100 transition-all group",
                                                            isUploading && "pointer-events-none opacity-50"
                                                        )}>
                                                            <div className="w-16 h-16 rounded-3xl bg-white flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
                                                                <FileUp className="w-8 h-8 text-accent" />
                                                            </div>
                                                            <div className="text-center">
                                                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-900 block mb-1">
                                                                    {isUploading ? 'Uploading Video...' : 'Select MP4 Video'}
                                                                </span>
                                                                <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Max allowed: 1000MB</span>
                                                            </div>
                                                            {uploadStatus.video && <CheckCircle2 className="w-10 h-10 text-emerald-500 absolute top-4 right-4 animate-in zoom-in" />}
                                                        </label>
                                                        {currentLesson.video_url && !uploadStatus.video && (
                                                            <p className="text-[8px] font-bold text-slate-400 truncate px-2">Current Video: {currentLesson.video_url}</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="space-y-4">
                                                <div className="flex items-center justify-between">
                                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Lesson Files (PDF / Links)</label>
                                                    {editingLessonId !== 'new' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => setManagingLessonAssets(editingLessonId)}
                                                            className="text-accent hover:text-accent/80 font-black uppercase tracking-widest text-[9px] h-auto p-0"
                                                        >
                                                            Manage Lesson Files
                                                        </Button>
                                                    )}
                                                </div>
                                                <div className="p-6 bg-slate-50 rounded-[1.5rem] border border-slate-100 italic text-[10px] font-medium text-slate-400 text-center">
                                                    Save lesson to enable file management.
                                                </div>
                                            </div>

                                            <div className="flex gap-4 pt-6">
                                                <Button
                                                    onClick={handleSaveLesson}
                                                    disabled={isSaving}
                                                    className="btn-gold h-16 flex-1 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] shadow-premium"
                                                >
                                                    {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Save Lesson'}
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    onClick={() => setEditingLessonId(null)}
                                                    className="h-16 flex-1 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] border-slate-200"
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="h-full flex flex-col items-center justify-center p-20 bg-slate-50/50 border-2 border-dashed border-slate-100 rounded-[3rem] text-center">
                                        <div className="w-20 h-20 bg-white rounded-[2.5rem] flex items-center justify-center shadow-soft mb-6">
                                            <Play className="w-8 h-8 text-slate-200" />
                                        </div>
                                        <h4 className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2">Editor Standby</h4>
                                        <p className="text-[9px] font-medium text-slate-400 max-w-[240px]">Select a lesson from the list to edit its content or add a new lesson.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Asset Management Overlay/Modal */}
            {managingLessonAssets && (
                <div className="fixed inset-0 z-[100] bg-slate-900/40 backdrop-blur-md flex items-center justify-center p-6 animate-in fade-in duration-300">
                    <div className="bg-white w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="p-10 border-b border-slate-100 flex items-center justify-between bg-white sticky top-0 z-10">
                            <div>
                                <h4 className="text-2xl font-black text-slate-900 uppercase tracking-tighter">Lesson Files & Links</h4>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Manage files for this lesson</p>
                            </div>
                            <button onClick={() => setManagingLessonAssets(null)} className="p-3 bg-slate-50 hover:bg-slate-100 rounded-2xl text-slate-400 transition-all"><X className="w-6 h-6" /></button>
                        </div>

                        <div className="p-10 space-y-10 overflow-y-auto max-h-[70vh]">
                            <div className="bg-slate-50 p-8 rounded-[2rem] border border-slate-100 space-y-6">
                                <div className="flex gap-2 p-1.5 bg-white rounded-2xl w-fit">
                                    <button
                                        onClick={() => setNewAsset({ ...newAsset, type: 'pdf' })}
                                        className={cn(
                                            "px-6 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all",
                                            newAsset.type === 'pdf' ? "bg-primary text-white shadow-md" : "text-slate-400"
                                        )}
                                    >PDF Document</button>
                                    <button
                                        onClick={() => setNewAsset({ ...newAsset, type: 'link' })}
                                        className={cn(
                                            "px-6 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all",
                                            newAsset.type === 'link' ? "bg-primary text-white shadow-md" : "text-slate-400"
                                        )}
                                    >External Link</button>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">File Title</label>
                                    <Input
                                        placeholder="Lesson PDF..."
                                        value={newAsset.title || ''}
                                        onChange={e => setNewAsset({ ...newAsset, title: e.target.value })}
                                        className="h-14 rounded-2xl border-white focus:border-accent shadow-sm"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">
                                        {newAsset.type === 'pdf' ? 'Upload PDF' : 'Website URL'}
                                    </label>
                                    {newAsset.type === 'pdf' ? (
                                        <div className="relative">
                                            <input
                                                type="file"
                                                id="asset-upload"
                                                className="hidden"
                                                accept=".pdf"
                                                onChange={async (e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file && courseId && managingLessonAssets) {
                                                        const path = `courses/${courseId}/lessons/${managingLessonAssets}/docs/${file.name}`;
                                                        const url = await handleFileUpload(file, 'course-documents', path, 'asset');
                                                        if (url) setNewAsset({ ...newAsset, file_url: url });
                                                    }
                                                }}
                                            />
                                            <label htmlFor="asset-upload" className={cn(
                                                "flex items-center justify-center gap-3 h-14 bg-white border border-dashed border-slate-200 rounded-2xl cursor-pointer hover:bg-slate-50 transition-all text-[10px] font-black uppercase tracking-widest text-slate-400 shadow-sm",
                                                isUploading && "pointer-events-none opacity-50"
                                            )}>
                                                {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                                {uploadStatus.asset ? 'File Ready ✅' : 'Upload PDF'}
                                            </label>
                                        </div>
                                    ) : (
                                        <Input
                                            placeholder="https://..."
                                            value={newAsset.file_url || ''}
                                            onChange={e => setNewAsset({ ...newAsset, file_url: e.target.value })}
                                            className="h-14 rounded-2xl border-white focus:border-accent shadow-sm"
                                        />
                                    )}
                                </div>

                                <Button onClick={handleAddAsset} className="btn-gold w-full h-14 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-premium">
                                    Add File to Lesson
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default AdminCourseEditor;
